import { useTranslation } from 'react-i18next';
import i18n from '../../i18n';
import React, { useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Calendar, ArrowUpDown, ArrowUp, ArrowDown } from 'lucide-react';
import LabourCostTimelineChart from '../LabourCostTimelineChart';
import DisplayCurrencySwitcher from '../DisplayCurrencySwitcher';
import { useDisplayCurrency, SYMBOL } from '../../hooks/useDisplayCurrency';
import { CopyNumberButton } from '../shared/CopyNumberButton';
import { ResetFiltersButton } from '../shared/ResetFiltersButton';
import useRememberedInputs from '../../hooks/useRememberedInputs';

const SORT_OPTIONS = [
  { value: 'start_date', labelKey: 'auto.lbl.startDate' },
  { value: 'name', labelKey: 'auto.lbl.alphabetically' },
  { value: 'end_date', labelKey: 'auto.lbl.endDate' },
  { value: 'revenue', labelKey: 'auto.lbl.revenue' },
  { value: 'labor', labelKey: 'auto.lbl.laborCost' },
  { value: 'expenses', labelKey: 'auto.lbl.expenses' },
  { value: 'costs', labelKey: 'auto.lbl.totalCosts' },
  { value: 'margin', labelKey: 'auto.lbl.margin' }
];

const ProjectBudgetsTab = ({
  projects,
  budgetSummaries,
  budgetLoading,
  formatCurrency,
  dateRange,
  onDateRangeChange,
  sortBy = 'start_date',
  sortOrder = 'desc',
  onSortChange,
  onResetFilters
}) => {
  const { t } = useTranslation();
  const { currency: displayCurrency } = useDisplayCurrency();
  // Active/all filter — persist per user in localStorage so admins
  // don't have to re-toggle every visit.
  const [statusFilter, setStatusFilter] = React.useState('active');
  useRememberedInputs('budget-project-status', { statusFilter: [statusFilter, setStatusFilter] });
  // Pick the display-currency-converted value when the backend
  // returned one (new budget-summary endpoint); fall back to the
  // PLN canonical value so old/cached responses still render.
  const displayAmount = (summary, key) => {
    if (!summary) return 0;
    const disp = summary[`${key}_display`];
    if (disp != null) return disp;
    return summary[`${key}_pln`] || 0;
  };
  const fmt = (v) => formatCurrency
    ? formatCurrency(v, displayCurrency)
    : `${Number(v || 0).toLocaleString('pl-PL', { maximumFractionDigits: 0 })} ${SYMBOL[displayCurrency] || displayCurrency}`;
  const money = (v, testId) => (
    <span className="inline-flex items-center gap-0.5 whitespace-nowrap">
      {fmt(v)}<CopyNumberButton value={v} size="sm" testId={testId} />
    </span>
  );
  // Year options for quick selection
  const currentYear = new Date().getFullYear();
  const yearOptions = [currentYear - 2, currentYear - 1, currentYear, currentYear + 1];
  
  // Check if "Show All" is active (no date range set)
  const isShowingAll = !dateRange?.startDate && !dateRange?.endDate;
  const selectedYear = dateRange?.startDate ? parseInt(dateRange.startDate.substring(0, 4)) : null;

  // Handle year quick select
  const handleYearSelect = (year) => {
    if (onDateRangeChange) {
      onDateRangeChange({
        startDate: `${year}-01-01`,
        endDate: `${year}-12-31`
      });
    }
  };
  
  // Handle show all (no date filter)
  const handleShowAll = () => {
    if (onDateRangeChange) {
      onDateRangeChange({
        startDate: '',
        endDate: ''
      });
    }
  };

  // Sort projects based on budget data
  const sortedProjects = useMemo(() => {
    // "active" = project not marked completed/archived AND (no
    // end_date OR end_date is in the future). This matches the
    // definition used across the app for the Projects tab default
    // list.
    const now = Date.now();
    const isActiveProject = (p) => {
      const s = (p.status || '').toLowerCase();
      if (s === 'completed' || s === 'archived' || s === 'cancelled') return false;
      if (p.end_date) {
        const t = new Date(p.end_date).getTime();
        if (!Number.isNaN(t) && t < now) return false;
      }
      return true;
    };
    const statusFiltered = statusFilter === 'active'
      ? projects.filter(isActiveProject)
      : projects;
    const projectsWithData = statusFiltered.map(project => ({
      ...project,
      summary: budgetSummaries[project.id] || null
    }));
    
    // Filter out projects with no activity in the selected period (unless showing all)
    const filteredProjects = isShowingAll 
      ? projectsWithData 
      : projectsWithData.filter(p => {
          const summary = p.summary;
          // Only include projects that have some activity in the filtered period
          return summary && (
            summary.revenue_pln > 0 || 
            summary.total_costs_pln > 0 || 
            summary.total_hours > 0
          );
        });

    return filteredProjects.sort((a, b) => {
      const summaryA = a.summary || {};
      const summaryB = b.summary || {};
      
      let comparison = 0;
      
      switch (sortBy) {
        case 'name':
          comparison = (a.name || '').localeCompare(b.name || '');
          break;
        case 'start_date':
          const startA = summaryA.start_date || a.start_date || '9999-12-31';
          const startB = summaryB.start_date || b.start_date || '9999-12-31';
          comparison = startA.localeCompare(startB);
          break;
        case 'end_date':
          const endA = summaryA.end_date || a.end_date || '9999-12-31';
          const endB = summaryB.end_date || b.end_date || '9999-12-31';
          comparison = endA.localeCompare(endB);
          break;
        case 'revenue':
          comparison = (summaryA.revenue_pln || 0) - (summaryB.revenue_pln || 0);
          break;
        case 'labor':
          comparison = (summaryA.labor_cost_pln || 0) - (summaryB.labor_cost_pln || 0);
          break;
        case 'expenses':
          comparison = (summaryA.expense_cost_pln || 0) - (summaryB.expense_cost_pln || 0);
          break;
        case 'costs':
          comparison = (summaryA.total_costs_pln || 0) - (summaryB.total_costs_pln || 0);
          break;
        case 'margin':
          comparison = (summaryA.profit_margin_percent || 0) - (summaryB.profit_margin_percent || 0);
          break;
        default:
          comparison = 0;
      }
      
      return sortOrder === 'desc' ? -comparison : comparison;
    });
  }, [projects, budgetSummaries, sortBy, sortOrder, isShowingAll, statusFilter]);

  // Format date for display
  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('pl-PL', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  if (budgetLoading) {
    return (
      <div className="text-center py-8 text-gray-500">{t('budget.loadingBudgetData')}</div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Filter and Sort Controls */}
      <Card className="bg-gray-50">
        <CardContent className="p-4">
          <div className="flex flex-wrap items-center gap-4">
            {/* Date Filter */}
            <div className="flex items-center gap-2">
              <Calendar className="h-4 w-4 text-gray-500" />
              <span className="text-sm font-medium text-gray-700">{t('budget.period')}</span>
            </div>
            
            {/* Show All button */}
            <Button
              variant={isShowingAll ? "default" : "outline"}
              size="sm"
              onClick={handleShowAll}
              className="px-3"
             data-testid="btn-all-time">
              {t('budget.allTime')}
            </Button>
            
            <div className="h-6 w-px bg-gray-300" />
            
            {/* Year quick select buttons */}
            <div className="flex gap-1">
              {yearOptions.map(year => (
                <Button
                  key={year}
                  variant={selectedYear === year && !isShowingAll ? "default" : "outline"}
                  size="sm"
                  onClick={() => handleYearSelect(year)}
                  className="px-3"
                >
                  {year}
                </Button>
              ))}
            </div>
            
            <div className="h-6 w-px bg-gray-300" />
            
            {/* Custom date range */}
            <div className="flex items-center gap-2">
              <Label className="text-sm text-gray-500">{t('budget.from')}</Label>
              <Input
                type="date"
                value={dateRange?.startDate || ''}
                onChange={(e) => onDateRangeChange?.({ ...dateRange, startDate: e.target.value })}
                className="h-8 w-44"
              />
            </div>
            <div className="flex items-center gap-2">
              <Label className="text-sm text-gray-500">{t('budget.to')}</Label>
              <Input
                type="date"
                value={dateRange?.endDate || ''}
                onChange={(e) => onDateRangeChange?.({ ...dateRange, endDate: e.target.value })}
                className="h-8 w-44"
              />
            </div>
            
            <div className="h-6 w-px bg-gray-300" />
            
            {/* Sort Controls */}
            <div className="flex items-center gap-2">
              <ArrowUpDown className="h-4 w-4 text-gray-500" />
              <Label className="text-sm text-gray-500">{t('budget.sort')}</Label>
              <Select value={sortBy} onValueChange={(v) => onSortChange?.(v, sortOrder)}>
                <SelectTrigger className="h-8 w-36">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {SORT_OPTIONS.map(opt => (
                    <SelectItem key={opt.value} value={opt.value}>{t(opt.labelKey)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button
                variant="outline"
                size="sm"
                onClick={() => onSortChange?.(sortBy, sortOrder === 'asc' ? 'desc' : 'asc')}
                className="h-8 px-2"
              >
                {sortOrder === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />}
              </Button>
            </div>

            <div className="h-6 w-px bg-gray-300" />

            {/* Active/All status filter */}
            <div className="flex items-center gap-2" data-testid="project-budgets-status-filter">
              <Label className="text-sm text-gray-500">{t('budget.show')}</Label>
              {[
                { key: 'active', label: t('auto.lbl.activeCurrent') },
                { key: 'all',    label: t('auto.lbl.all') },
              ].map((opt) => (
                <Button
                  key={opt.key}
                  variant={statusFilter === opt.key ? 'default' : 'outline'}
                  size="sm"
                  className="h-8"
                  onClick={() => setStatusFilter(opt.key)}
                  data-testid={`project-budgets-status-${opt.key}`}
                  data-active={statusFilter === opt.key}
                >
                  {opt.label}
                </Button>
              ))}
            </div>

            <div className="h-6 w-px bg-gray-300" />

            {/* Display currency — presentation-only filter */}
            <DisplayCurrencySwitcher />
            {onResetFilters && (
              <ResetFiltersButton testId="project-budgets-reset-filters"
                onClick={() => { onResetFilters(); setStatusFilter('active'); }} />
            )}
          </div>
        </CardContent>
      </Card>

      {/* Project Cards */}
      {sortedProjects.length === 0 ? (
        <div className="text-center py-8 text-gray-500">
          {t('budget.noProjectsAvailable')}
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4">
          {sortedProjects.map(project => {
            const summary = project.summary;
            
            return (
              <Card key={project.id} data-testid={`project-budget-${project.id}`}>
                <CardHeader className="pb-2">
                  <div className="flex justify-between items-start">
                    <div>
                      <CardTitle className="flex items-center gap-2">
                        {project.name}
                      </CardTitle>
                      <CardDescription className="flex items-center gap-4 mt-1">
                        <span>
                          {t('budget.budgetTypeLabel')} {summary?.budget_type === 'fixed' ? t('budget.fixedPrice') : t('budget.hourlyRate')}
                        </span>
                        {/* Project Dates */}
                        <span className="flex items-center gap-1 text-xs">
                          <Calendar className="h-3 w-3" />
                          {formatDate(summary?.start_date || project.start_date)} 
                          {' → '} 
                          {summary?.actual_end_date || project.actual_end_date ? (
                            <span className="text-green-600">{formatDate(summary?.actual_end_date || project.actual_end_date)} ({t('budget.finished')})</span>
                          ) : summary?.planned_end_date || project.planned_end_date ? (
                            <span className="text-orange-500">{formatDate(summary?.planned_end_date || project.planned_end_date)} ({t('budget.planned')})</span>
                          ) : (
                            <span className="text-gray-400">{t('budget.ongoing')}</span>
                          )}
                        </span>
                      </CardDescription>
                    </div>
                    {summary && (
                      <Badge className="gap-1" variant={summary.profit_pln >= 0 ? 'default' : 'destructive'}>
                        {t('budget.profitLabel')} {money(displayAmount(summary, 'profit'), `copy-budget-profit-${project.id}`)}
                      </Badge>
                    )}
                  </div>
                </CardHeader>
                <CardContent>
                  {summary ? (
                    <>
                      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                        <div>
                          <p className="text-sm text-gray-500">{t('budget.revenue')}</p>
                          <p className="text-lg font-semibold text-green-600">
                            {money(displayAmount(summary, 'revenue'), `copy-budget-revenue-${project.id}`)}
                          </p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-500">{t('budget.laborCost')}</p>
                          <p className="text-lg font-semibold">
                            {money(displayAmount(summary, 'labor_cost'), `copy-budget-labor-${project.id}`)}
                          </p>
                          <p className="text-xs text-gray-400">{t('budget.hoursCount', { count: Number(summary.total_hours) || 0, hours: new Intl.NumberFormat(i18n.language?.startsWith('pl') ? 'pl-PL' : 'en-GB', { maximumFractionDigits: 2 }).format(Number(summary.total_hours) || 0) })}</p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-500">{t('budget.expenses')}</p>
                          <p className="text-lg font-semibold">
                            {money(displayAmount(summary, 'expense_cost'), `copy-budget-expenses-${project.id}`)}
                          </p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-500">{t('budget.totalCosts')}</p>
                          <p className="text-lg font-semibold text-red-600">
                            {money(displayAmount(summary, 'total_costs'), `copy-budget-costs-${project.id}`)}
                          </p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-500">{t('budget.margin')}</p>
                          <p className={`text-lg font-semibold ${summary.profit_margin_percent >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                            {summary.profit_margin_percent?.toFixed(1) || 0}%
                          </p>
                        </div>
                      </div>
                      
                      {/* Expense breakdown by category */}
                      {summary.expense_by_category && Object.keys(summary.expense_by_category).length > 0 && (
                        <div className="mt-4 pt-4 border-t">
                          <p className="text-sm font-medium mb-2">{t('budget.expenseBreakdown')}</p>
                          <div className="flex flex-wrap gap-2">
                            {Object.entries(summary.expense_by_category_display || summary.expense_by_category).map(([cat, amount]) => (
                              <Badge key={cat} variant="outline" className="gap-1">
                                {cat}: {money(amount, `copy-budget-cat-${project.id}-${cat}`)}
                              </Badge>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Labour cost over time — stacked area per team
                          member with vertical markers on rate changes */}
                      <div className="mt-4 pt-4 border-t">
                        <LabourCostTimelineChart projectId={project.id} projectName={project.name} />
                      </div>
                    </>
                  ) : (
                    <div className="text-center py-4 text-gray-400">
                      {t('budget.noBudgetDataForThisPeriod')}
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default ProjectBudgetsTab;
